import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View, type BlurEvent, type FocusEvent, type TextInput, type ViewProps } from 'react-native'
import { Input, type InputProps, type InputVariant } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { cn } from '~/lib/utils'

export interface OTPInputProps extends Omit<
  InputProps,
  'value' | 'onChange' | 'onChangeText' | 'maxLength' | 'ref'
> {
  children?: React.ReactNode
  containerClassName?: string
  maxLength: number
  onChange?: (value: string) => void
  onComplete?: (value: string) => void
  pattern?: string | RegExp
  textAlign?: 'left' | 'center' | 'right'
  value?: string
  ref?: React.Ref<TextInput | null>
  variant?: InputVariant
}

export function OTPInput({
  defaultValue = '',
  inputMode = 'numeric',
  maxLength,
  onChange,
  onComplete,
  pattern,
  value: controlledValue,
  ref,
  variant = 'line',
  ...props
}: OTPInputProps) {
  // Internal state for uncontrolled mode
  const [internalValue, setInternalValue] = useState(
    typeof defaultValue === 'string' ? defaultValue : ''
  )

  // Determine if controlled or uncontrolled
  const isControlled = controlledValue !== undefined
  const value = isControlled ? controlledValue : internalValue
  const previousValue = useRef(value)

  // Refs
  const inputRef = useRef<TextInput>(null)

  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
        ref(inputRef.current)
      } else if (typeof ref === 'object' && ref !== null) {
        ref.current = inputRef.current
      }
    }
  }, [ref])

  // Create regex pattern
  const regexp = useMemo(() => {
    if (!pattern) {
      return null
    }

    return typeof pattern === 'string' ? new RegExp(pattern) : pattern
  }, [pattern])

  // Handle value changes
  const handleChange = useCallback(
    (newValue: string) => {
      const trimmedValue = newValue.slice(0, maxLength)

      // Validate against pattern if provided
      if (trimmedValue.length > 0 && regexp && !regexp.test(trimmedValue)) {
        return
      }

      if (isControlled) {
        onChange?.(trimmedValue)
      } else {
        setInternalValue(trimmedValue)
        onChange?.(trimmedValue)

        if (
          previousValue.current !== trimmedValue &&
          previousValue.current.length < maxLength &&
          trimmedValue.length === maxLength
        ) {
          onComplete?.(trimmedValue)
        }

        previousValue.current = trimmedValue
      }
    },
    [isControlled, maxLength, onChange, regexp, onComplete]
  )

  // Focus handler
  const handleFocus = useCallback(
    (event: FocusEvent) => {
      props.onFocus?.(event)
    },
    [props]
  )

  // Blur handler
  const handleBlur = useCallback(
    (event: BlurEvent) => {
      props.onBlur?.(event)
    },
    [props]
  )

  const focusInput = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <Input
      {...props}
      onPress={focusInput}
      autoComplete="one-time-code"
      variant={variant}
      numberOfLines={1}
      inputMode={inputMode}
      maxLength={maxLength}
      onBlur={handleBlur}
      onChangeText={handleChange}
      onFocus={handleFocus}
      ref={inputRef}
      textContentType="oneTimeCode"
      value={value}
    />
  )
}

// Individual slot component
export interface OTPInputSlotProps extends ViewProps {
  className?: string
  index: number
  ref?: React.Ref<View>
  slots: {
    char: string | null
    hasFakeCaret: boolean
    isActive: boolean
    placeholderChar: string | null
  }[]
}

export function OTPInputSlot({
  children,
  className,
  index,
  ref,
  slots,
  ...props
}: OTPInputSlotProps) {
  const slot = slots[index]

  if (!slot) {
    return null
  }

  return (
    <View
      className={cn(
        'relative flex h-12 w-12 items-center justify-center border border-gray-300 text-sm',
        slot.isActive && 'border-blue-500 ring-1 ring-blue-500',
        className
      )}
      ref={ref}
      {...props}>
      {children || (
        <>
          {slot.char && (
            <Text className="text-center font-medium text-base text-foreground">{slot.char}</Text>
          )}
          {slot.hasFakeCaret && (
            <View className="absolute inset-0 flex items-center justify-center">
              <View className="h-4 w-0.5 animate-pulse bg-gray-900" />
            </View>
          )}
          {slot.placeholderChar && !slot.char && (
            <Text className="text-center text-base text-foreground">{slot.placeholderChar}</Text>
          )}
        </>
      )}
    </View>
  )
}

// Group component for layout
export interface OTPInputGroupProps extends ViewProps {
  className?: string
  ref?: React.Ref<View>
}

export function OTPInputGroup({ className, ref, ...props }: OTPInputGroupProps) {
  return <View className={cn('flex flex-row items-center gap-2', className)} ref={ref} {...props} />
}

// Separator component
export interface OTPInputSeparatorProps extends ViewProps {
  className?: string
  ref?: React.Ref<View>
}

export function OTPInputSeparator({ className, ref, ...props }: OTPInputSeparatorProps) {
  return <View className={cn('flex items-center justify-center', className)} ref={ref} {...props} />
}
